import moment from 'moment';
import _ from 'lodash';
import database from 'src/providers/watermelondb';
import {Q} from '@nozbe/watermelondb';

export const exportDuration = async ({
  sets = [],
  session = undefined,
  startDate = undefined,
  endDate = undefined,
}: {
  sets: any[];
  session?: any;
  startDate?: string | number | Date;
  endDate?: string | number | Date;
}) => {
  const val = (v: any, alt: any) => (v !== undefined && v !== null ? v : alt);
  const setStart = (s: any) =>
    val(s?.startTimestamp, s?.start_timestamp ?? s?.start);
  const setEnd = (s: any) => val(s?.endTimestamp, s?.end_timestamp ?? s?.end);

  // --- normalize prompts like your table does (HTTP may store JSON string) ---
  const normalizePromptsArray = (ps: any): any[] => {
    if (Array.isArray(ps)) {
      return ps;
    }
    if (ps == null) {
      return [];
    }
    if (typeof ps === 'string') {
      try {
        const parsed = JSON.parse(ps);
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        if (ps.includes(',')) {
          return ps
            .split(',')
            .map((s: string) => s.trim())
            .filter(Boolean);
        }
        return [ps]; // single id string
      }
    }
    return [];
  };

  // --- optional date filtering like the table (by set start) ---
  const inRangeSets =
    startDate && endDate
      ? sets.filter((s: any) => {
          const ts = Number(setStart(s)) || 0;
          const start = new Date(startDate);
          start.setHours(0, 0, 0, 0);
          const end = new Date(endDate);
          end.setHours(23, 59, 59, 999);
          return ts >= start.getTime() && ts <= end.getTime();
        })
      : sets;

  // --- collect distinct prompt IDs from all sets ---
  const collectPromptIds = (): string[] => {
    const ids = new globalThis.Set<string>(); // avoid any model named `Set`
    for (const s of inRangeSets) {
      const ps = normalizePromptsArray(s?.prompts);
      for (const p of ps) {
        if (typeof p === 'string') {
          ids.add(p);
        } else if (p?.id) {
          ids.add(p.id);
        }
      }
    }
    return Array.from(ids);
  };

  const promptNameById: Record<string, string> = {};
  const allPromptIds = collectPromptIds();

  // batch helper
  const fetchByIdBatches = async (
    table: string,
    ids: string[],
    idField: string = 'id',
  ) => {
    const out: any[] = [];
    for (const ch of _.chunk(ids, 500)) {
      const rows = await database
        .get(table)
        .query(Q.where(idField, Q.oneOf(ch)))
        .fetch();
      out.push(...rows);
    }
    return out;
  };

  // 1) Try `prompts` table directly
  if (allPromptIds.length > 0) {
    const promptRows = await fetchByIdBatches('prompts', allPromptIds, 'id');
    for (const row of promptRows) {
      promptNameById[row.id] = row?.name || row?.acronym || '';
    }

    // 2) Fallback to `program_prompt` for any IDs not resolved above
    const missing = allPromptIds.filter(id => !promptNameById[id]);
    if (missing.length > 0) {
      const ppRows = await fetchByIdBatches('program_prompt', missing, 'id');

      // use program_prompt.name (or acronym) if present
      for (const pp of ppRows) {
        const name = pp?.name || pp?.acronym || '';
        if (name) {
          promptNameById[pp.id] = name;
        }
      }

      // still missing? hop via pp.prompt_id -> prompts.id
      const stillMissing = missing.filter(id => !promptNameById[id]);
      if (stillMissing.length > 0 && ppRows.length > 0) {
        const baseIds = _.uniq(
          ppRows
            .map((pp: any) => pp?.prompt_id || pp?.promptId)
            .filter(Boolean),
        );
        if (baseIds.length > 0) {
          const basePrompts = await fetchByIdBatches('prompts', baseIds, 'id');
          const baseMap: Record<string, string> = {};
          for (const p of basePrompts) {
            baseMap[p.id] = p?.name || p?.acronym || '';
          }

          for (const pp of ppRows) {
            const name = baseMap[pp?.prompt_id || pp?.promptId];
            if (name && !promptNameById[pp.id]) {
              promptNameById[pp.id] = name;
            }
          }
        }
      }
    }
  }

  const namesForSetPrompts = (s: any): string[] => {
    const ps = normalizePromptsArray(s?.prompts);
    const out: string[] = [];
    for (const p of ps) {
      if (typeof p === 'string') {
        const name = promptNameById[p];
        if (name) {
          out.push(name);
        }
      } else if (p?.name) {
        out.push(p.name);
      } else if (p?.acronym) {
        out.push(p.acronym);
      } else if (p?.id && promptNameById[p.id]) {
        out.push(promptNameById[p.id]);
      }
    }
    return out;
  };

  // --- CSV header (kept original columns) ---
  const rows: any[] = [
    ['Date', 'Time Stamp', 'Instance', 'Intensity', 'Total', 'ABC'],
  ];

  for (let i = 0; i < inRangeSets.length; i++) {
    const set = inRangeSets[i];

    // Date column mirrors the table’s logic
    const dateStr = session
      ? moment(session?.sessionDate).format('ll')
      : moment(set?.session_date).format('ll');

    const startTs = setStart(set);
    const endTs = setEnd(set);

    const startStr = startTs ? moment(startTs).format('hh:mm:ss A') : '';
    const endStr = endTs ? moment(endTs).format('hh:mm:ss A') : 'Incomplete';
    const timeStamp = `${startStr} - ${endStr}`;

    // duration (match table: include hours/min/sec)
    let totalStr = 'Incomplete';
    if (startTs && endTs) {
      const dur = moment.duration(moment(endTs).diff(moment(startTs)));
      const h = dur.hours();
      const m = dur.minutes();
      const s = dur.seconds();
      totalStr =
        (h > 0 ? `${h} hr ` : '') +
        (m > 0 ? `${m} min ` : '') +
        `${s >= 0 ? `${s} sec` : ''}`.trim();
    }

    const intensity = _.capitalize(set?.intensity || '');

    // ABC prompts as newline-separated names (with quotes sanitized)
    const abc = namesForSetPrompts(set)
      .map(n => (typeof n === 'string' ? n.replaceAll('"', "'") : ''))
      .filter(Boolean)
      .join('\n');

    rows.push([
      dateStr,
      timeStamp,
      `${i + 1}`, // Instance
      intensity,
      totalStr,
      abc,
    ]);
  }

  return rows;
};
